import React from 'react';
import PropTypes from 'prop-types';
import FeaturePageShell from './FeaturePageShell';
import { getSearchPageSurface } from '../../app/routes/searchPageMap';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';

/**
 * SearchFeaturePageShell — thin runtime adapter between searchPageMap and FeaturePageShell.
 *
 * Accepts a `pageId` (canonical searchPageMap id) and resolves the shell surface
 * contract centrally, eliminating per-page boilerplate:
 *   - calls `getSearchPageSurface(pageId)` to get `{ titleKey, width, headerOwner }`
 *   - if `headerOwner === 'shell'`: localizes `titleKey` and passes `title` to FeaturePageShell
 *   - if `headerOwner === 'page'`: omits `title` (inner component owns its own header)
 *   - forwards `width` and all named FeaturePageShell props as-is
 *
 * Fails loudly for unknown ids or redirect aliases via `getSearchPageSurface`'s
 * existing throw contract.
 *
 * Usage:
 *   <SearchFeaturePageShell pageId="customizationSynonyms" isLoading={isFetching}>
 *     <SynonymsTable />
 *   </SearchFeaturePageShell>
 */
const SearchFeaturePageShell = ({
  pageId,
  description,
  actions,
  secondaryActions,
  children,
  sidebar,
  isLoading,
  error,
  onRetry,
}) => {
  const { titleKey, width, headerOwner } = getSearchPageSurface(pageId);
  const t = useLocalizedMessage();

  const title = headerOwner === 'shell' ? t(titleKey) : undefined;

  return (
    <FeaturePageShell
      title={title}
      description={description}
      actions={actions}
      secondaryActions={secondaryActions}
      sidebar={sidebar}
      width={width}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
    >
      {children}
    </FeaturePageShell>
  );
};

SearchFeaturePageShell.propTypes = {
  /** Canonical searchPageMap entry id. Determines width, title, and headerOwner. */
  pageId: PropTypes.string.isRequired,
  /** One-line contextual description shown below the title. */
  description: PropTypes.node,
  /** Primary page-level actions (save / apply / discard). */
  actions: PropTypes.node,
  /** Secondary page-level controls (help link, docs link). */
  secondaryActions: PropTypes.node,
  /** Page content — not rendered while isLoading or error is set. */
  children: PropTypes.node,
  /** Optional right-column sidebar content. */
  sidebar: PropTypes.node,
  /** Show loading spinner; suppresses children and error. */
  isLoading: PropTypes.bool,
  /** Error message or object; shows degraded state. */
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Retry callback shown with the error state. */
  onRetry: PropTypes.func,
};

SearchFeaturePageShell.defaultProps = {
  description: null,
  actions: null,
  secondaryActions: null,
  children: null,
  sidebar: null,
  isLoading: false,
  error: null,
  onRetry: null,
};

export default SearchFeaturePageShell;
